/**
 * Case List Component
 * Task 47: Legal case management list with filters and fee tracking
 */

import { useState, useMemo } from 'react';
import { IndustryLegalCase } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreHorizontal, Pencil, Trash2, Plus, Search, Scale } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface CaseListProps {
  cases: IndustryLegalCase[];
  onEdit?: (legalCase: IndustryLegalCase) => void;
  onDelete?: (legalCase: IndustryLegalCase) => void;
  onCreate?: () => void;
}

type StatusFilter = 'all' | 'active' | 'won' | 'lost' | 'settled' | 'archived';
type CaseStatus = 'active' | 'won' | 'lost' | 'settled' | 'archived';

const STATUS_LABELS: Record<CaseStatus, string> = {
  active: 'Em Andamento',
  won: 'Ganho',
  lost: 'Perdido',
  settled: 'Acordo',
  archived: 'Arquivado',
};

const STATUS_COLORS: Record<CaseStatus, string> = {
  active: 'default',
  won: 'default',
  lost: 'destructive',
  settled: 'secondary',
  archived: 'outline',
};

export function CaseList({ cases, onEdit, onDelete, onCreate }: CaseListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>('all');

  // Extract unique case types
  const caseTypes = useMemo(() => {
    const types = new Set<string>();
    cases.forEach((c) => {
      const type = c.metadata?.caseType as string;
      if (type) types.add(type);
    });
    return Array.from(types).sort();
  }, [cases]);

  // Filter cases
  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesClient = c.metadata?.clientName?.toString().toLowerCase().includes(term);
        const matchesCaseNumber = c.metadata?.caseNumber?.toString().toLowerCase().includes(term);
        const matchesCourt = c.metadata?.court?.toString().toLowerCase().includes(term);
        if (!matchesClient && !matchesCaseNumber && !matchesCourt) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;

      // Case type filter
      if (caseTypeFilter !== 'all') {
        const caseType = c.metadata?.caseType as string;
        if (caseType !== caseTypeFilter) return false;
      }

      return true;
    });
  }, [cases, searchTerm, statusFilter, caseTypeFilter]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Casos Jurídicos
              </CardTitle>
              <CardDescription>Gerencie processos e honorários</CardDescription>
            </div>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Caso
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar casos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="active">Em Andamento</SelectItem>
                <SelectItem value="won">Ganhos</SelectItem>
                <SelectItem value="lost">Perdidos</SelectItem>
                <SelectItem value="settled">Acordos</SelectItem>
                <SelectItem value="archived">Arquivados</SelectItem>
              </SelectContent>
            </Select>

            {/* Case Type Filter */}
            {caseTypes.length > 0 && (
              <Select value={caseTypeFilter} onValueChange={setCaseTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  {caseTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Mostrando {filteredCases.length} de {cases.length} casos
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredCases.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm || statusFilter !== 'all' || caseTypeFilter !== 'all'
                ? 'Nenhum caso encontrado com os filtros selecionados'
                : 'Nenhum caso cadastrado'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número/Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Comarca/Vara</TableHead>
                    <TableHead>Data Início</TableHead>
                    <TableHead>Honorários</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCases.map((legalCase) => {
                    const caseNumber = legalCase.metadata?.caseNumber as string;
                    const clientName = legalCase.metadata?.clientName as string;
                    const caseType = legalCase.metadata?.caseType as string;
                    const court = legalCase.metadata?.court as string;
                    const fees = legalCase.metadata?.fees as number;

                    return (
                      <TableRow key={legalCase.id}>
                        <TableCell>
                          <div>
                            {caseNumber && (
                              <div className="font-medium">{caseNumber}</div>
                            )}
                            {clientName && (
                              <div className="text-sm text-muted-foreground">{clientName}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {caseType ? (
                            <Badge variant="outline">{caseType}</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{court || '-'}</TableCell>
                        <TableCell>{formatDate(legalCase.start_date)}</TableCell>
                        <TableCell className="font-medium">
                          {fees ? formatCurrency(fees) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_COLORS[legalCase.status as CaseStatus] as any}>
                            {STATUS_LABELS[legalCase.status as CaseStatus]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {onEdit && (
                                <DropdownMenuItem onClick={() => onEdit(legalCase)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {onDelete && (
                                <DropdownMenuItem
                                  onClick={() => onDelete(legalCase)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
